import { useEffect, useState } from "react";
import { Mail, Send, Plus, History, Zap, Pencil, Trash2, CircleUserRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";
const labelClass = "mb-1.5 block text-sm font-bold text-paper/85";

const TRIGGER_LABELS = { inquiry_created: "Auto: on new inquiry", order_reserved: "Auto: on package reservation" };

export function FollowupsPanel({ prefill, onViewUser }) {
  const [templates, setTemplates] = useState(null);
  const [sends, setSends] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [toEmail, setToEmail] = useState(prefill?.to_email || "");
  const [toName, setToName] = useState(prefill?.to_name || "");
  const [inquiryId, setInquiryId] = useState(prefill?.inquiry_id || null);
  const [status, setStatus] = useState(null); // {kind: "ok"|"err", text}
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTpl, setNewTpl] = useState({ name: "", subject: "", body: "", sequence_trigger: "", sequence_delay_days: "" });

  const startEdit = (t) => {
    setEditingId(t.id);
    setNewTpl({
      name: t.name,
      subject: t.subject,
      body: t.body,
      sequence_trigger: t.sequence_trigger || "",
      sequence_delay_days: t.sequence_delay_days ?? "",
    });
    setShowNew(true);
    setStatus(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setNewTpl({ name: "", subject: "", body: "", sequence_trigger: "", sequence_delay_days: "" });
    setShowNew(false);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template? This can't be undone.`)) return;
    const res = await fetch(`${API}/api/admin/followup/templates/${t.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      setTemplates((prev) => {
        const next = prev.filter((x) => x.id !== t.id);
        if (templateId === t.id) setTemplateId(next[0]?.id || "");
        return next;
      });
      if (editingId === t.id) resetForm();
    }
  };

  useEffect(() => {
    if (prefill?.to_email) {
      setToEmail(prefill.to_email);
      setToName(prefill.to_name || "");
      setInquiryId(prefill.inquiry_id || null);
    }
  }, [prefill]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/followup/templates`, { credentials: "include" });
        const data = res.ok ? await res.json() : [];
        setTemplates(data);
        if (data.length && !templateId) setTemplateId(data[0].id);
      } catch {
        setTemplates([]);
      }
      try {
        const res = await fetch(`${API}/api/admin/followup/sends`, { credentials: "include" });
        setSends(res.ok ? await res.json() : []);
      } catch {
        setSends([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = templates?.find((t) => t.id === templateId);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/admin/followup/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ template_id: templateId, to_email: toEmail, to_name: toName, inquiry_id: inquiryId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Send failed.");
      setStatus({ kind: "ok", text: `Sent to ${toEmail}.` });
      setSends((prev) => [
        { id: data.id, template_name: selected?.name, to_email: toEmail, to_name: toName, sent_at: new Date().toISOString() },
        ...(prev || []),
      ]);
    } catch (err) {
      setStatus({ kind: "err", text: err.message || "Send failed. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(
        editingId ? `${API}/api/admin/followup/templates/${editingId}` : `${API}/api/admin/followup/templates`,
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: newTpl.name,
            subject: newTpl.subject,
            body: newTpl.body,
            sequence_trigger: newTpl.sequence_trigger || null,
            sequence_delay_days: newTpl.sequence_delay_days === "" ? null : Number(newTpl.sequence_delay_days),
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Couldn't save that template.");
      if (editingId) {
        setTemplates((prev) => prev.map((t) => (t.id === editingId ? data : t)));
      } else {
        setTemplates((prev) => [...(prev || []), data]);
        setTemplateId(data.id);
      }
      resetForm();
      setStatus({ kind: "ok", text: `Template "${data.name}" saved.` });
    } catch (err) {
      setStatus({ kind: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-followups-panel" className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-3xl border border-white/10 bg-card p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 whitespace-nowrap font-display text-base font-extrabold text-paper sm:text-lg">
            <Send className="h-[18px] w-[18px] text-lilac" aria-hidden="true" />
            Send a follow-up
          </h3>
          <button
            type="button"
            data-testid="followup-new-template-toggle"
            onClick={() => {
              if (showNew) resetForm();
              else setShowNew(true);
            }}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-paper/60 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New template
          </button>
        </div>

        {templates === null ? (
          <p className="mt-6 text-sm text-paper/45">Loading…</p>
        ) : (
          <form onSubmit={send} className="mt-6 space-y-4">
            <div>
              <label htmlFor="followup-template" className={labelClass}>Template</label>
              <select
                id="followup-template"
                data-testid="followup-template-select"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className={`${fieldClass} appearance-none`}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            {selected && (
              <div data-testid="followup-preview" className="rounded-xl border border-white/[0.07] bg-ink/50 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-paper/40">Preview</p>
                <p className="mt-2 text-sm font-bold text-paper/85">{selected.subject.replace("{{name}}", toName || "there")}</p>
                <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-paper/50">
                  {selected.body.replace("{{name}}", toName || "there")}
                </p>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="followup-email" className={labelClass}>To email</label>
                <input
                  id="followup-email"
                  data-testid="followup-email-input"
                  type="email"
                  required
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="founder@example.com"
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="followup-name" className={labelClass}>Their name</label>
                <input
                  id="followup-name"
                  data-testid="followup-name-input"
                  type="text"
                  value={toName}
                  onChange={(e) => setToName(e.target.value)}
                  placeholder="Used for {{name}}"
                  className={fieldClass}
                />
              </div>
            </div>
            {status && (
              <p
                role="status"
                data-testid="followup-send-status"
                className={`rounded-xl border p-3.5 text-sm ${
                  status.kind === "ok" ? "border-[#8FD8B8]/30 bg-[#8FD8B8]/10 text-[#8FD8B8]" : "border-red-400/30 bg-red-400/10 text-red-200"
                }`}
              >
                {status.text}
              </p>
            )}
            <button
              type="submit"
              data-testid="followup-send-button"
              disabled={busy || !selected}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lilac px-7 py-3 text-sm font-bold text-ink transition-[transform,background-color,opacity] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send follow-up"}
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        )}

        {showNew && (
          <form onSubmit={createTemplate} data-testid="followup-template-form" className="mt-6 space-y-4 border-t border-white/[0.07] pt-6">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-paper/40">
              {editingId ? "Edit template" : "New template"}
            </p>
            <div>
              <label htmlFor="tpl-name" className={labelClass}>Template name</label>
              <input id="tpl-name" data-testid="tpl-name-input" required maxLength={120} value={newTpl.name} onChange={(e) => setNewTpl((v) => ({ ...v, name: e.target.value }))} className={fieldClass} placeholder="e.g. Second check-in" />
            </div>
            <div>
              <label htmlFor="tpl-subject" className={labelClass}>Subject</label>
              <input id="tpl-subject" data-testid="tpl-subject-input" required maxLength={200} value={newTpl.subject} onChange={(e) => setNewTpl((v) => ({ ...v, subject: e.target.value }))} className={fieldClass} placeholder="Hi {{name}}, …" />
            </div>
            <div>
              <label htmlFor="tpl-body" className={labelClass}>Body (use {"{{name}}"} for their name)</label>
              <textarea id="tpl-body" data-testid="tpl-body-input" required rows={5} maxLength={4000} value={newTpl.body} onChange={(e) => setNewTpl((v) => ({ ...v, body: e.target.value }))} className={`${fieldClass} resize-y`} placeholder="Hi {{name}}, …" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="tpl-trigger" className={labelClass}>Automation trigger (framework)</label>
                <select id="tpl-trigger" data-testid="tpl-trigger-select" value={newTpl.sequence_trigger} onChange={(e) => setNewTpl((v) => ({ ...v, sequence_trigger: e.target.value }))} className={`${fieldClass} appearance-none`}>
                  <option value="">Manual only for now</option>
                  <option value="inquiry_created">On new inquiry</option>
                  <option value="order_reserved">On package reservation</option>
                </select>
              </div>
              <div>
                <label htmlFor="tpl-delay" className={labelClass}>Delay (days)</label>
                <input id="tpl-delay" data-testid="tpl-delay-input" type="number" min="0" max="60" value={newTpl.sequence_delay_days} onChange={(e) => setNewTpl((v) => ({ ...v, sequence_delay_days: e.target.value }))} className={fieldClass} placeholder="0" />
              </div>
            </div>
            <p className="text-xs text-paper/40">
              Templates with a trigger also send automatically — the scheduler checks every 5 minutes and only emails events that happen after it starts (no backfill to old contacts).
            </p>
            <button
              type="submit"
              data-testid="tpl-save-button"
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-lilac/40 bg-lilac/10 px-7 py-3 text-sm font-bold text-lilac transition-colors duration-200 hover:bg-lilac/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac disabled:opacity-60"
            >
              {editingId ? "Save changes" : "Save template"}
            </button>
            {editingId && (
              <button
                type="button"
                data-testid="tpl-cancel-edit"
                onClick={resetForm}
                className="w-full py-1 text-center text-sm font-bold text-paper/50 transition-colors duration-200 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
              >
                Cancel editing
              </button>
            )}
          </form>
        )}
      </div>

      <div className="space-y-6">
        <div className="rounded-3xl border border-white/10 bg-card p-7">
          <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-paper">
            <Mail className="h-[18px] w-[18px] text-lilac" aria-hidden="true" />
            Saved templates
          </h3>
          {templates === null ? (
            <p className="mt-4 text-sm text-paper/45">Loading…</p>
          ) : (
            <ul data-testid="followup-templates-list" className="mt-4 space-y-3">
              {templates.map((t) => (
                <li key={t.id} data-testid={`followup-template-${t.id}`} className="rounded-xl border border-white/[0.07] bg-ink/50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-bold text-paper/85">{t.name}</p>
                    <div className="flex items-center gap-1.5">
                      {t.sequence_trigger ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-lilac/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-lilac">
                          <Zap className="h-3 w-3" aria-hidden="true" />
                          {TRIGGER_LABELS[t.sequence_trigger]}{t.sequence_delay_days ? ` · +${t.sequence_delay_days}d` : ""} · on
                        </span>
                      ) : (
                        <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-paper/40">Manual</span>
                      )}
                      <button
                        type="button"
                        data-testid={`template-edit-${t.id}`}
                        aria-label={`Edit ${t.name}`}
                        onClick={() => startEdit(t)}
                        className="rounded-full p-1.5 text-paper/45 transition-colors duration-200 hover:bg-white/[0.06] hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        data-testid={`template-delete-${t.id}`}
                        aria-label={`Delete ${t.name}`}
                        onClick={() => deleteTemplate(t)}
                        className="rounded-full p-1.5 text-paper/45 transition-colors duration-200 hover:bg-red-400/10 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-paper/45">{t.subject}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-card p-7">
          <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-paper">
            <History className="h-[18px] w-[18px] text-lilac" aria-hidden="true" />
            Send history
          </h3>
          {sends === null ? (
            <p className="mt-4 text-sm text-paper/45">Loading…</p>
          ) : sends.length === 0 ? (
            <p data-testid="followup-sends-empty" className="mt-4 text-sm text-paper/50">No follow-ups sent yet.</p>
          ) : (
            <ul data-testid="followup-sends-list" className="mt-4 space-y-2.5">
              {sends.slice(0, 12).map((s) => (
                <li key={s.id} data-testid={`followup-send-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ink/50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-paper/80">{s.to_name || s.to_email}</p>
                    <p className="text-xs text-paper/45">{s.template_name} → {s.to_email}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {s.user_id && onViewUser && (
                      <button
                        type="button"
                        data-testid={`send-view-user-${s.id}`}
                        onClick={() => onViewUser(s.user_id)}
                        aria-label={`View profile for ${s.to_email}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-[11px] font-bold text-paper/55 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                      >
                        <CircleUserRound className="h-3.5 w-3.5" aria-hidden="true" />
                        Profile
                      </button>
                    )}
                    <span className="text-xs text-paper/35">
                      {new Date(s.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
